import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Tooltip from '@mui/material/Tooltip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DataObjectIcon from '@mui/icons-material/DataObject'
import CloseIcon from '@mui/icons-material/Close'
import { Markdown } from '@/components/Markdown'
import type { LogEntry, SystemEntryData, AssistantTextEntryData, ToolCallEntryData, UserTextEntryData } from './types'

function formatTokens(count: number): string {
  if (count < 1000) return `${count}`
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}K`
  return `${(count / 1_000_000).toFixed(2)}M`
}

function CollapsibleCode({ content, label }: { content: string; label: string }) {
  const [expanded, setExpanded] = useState(false)
  const lineCount = content.split('\n').length
  const isLong = lineCount > 10

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: isLong ? 'pointer' : 'default' }}
        onClick={() => isLong && setExpanded(!expanded)}
      >
        <Typography variant="caption" color="text.secondary">
          {label} ({lineCount} lines)
        </Typography>
        {isLong && (
          <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        )}
      </Box>
      {isLong ? (
        <Collapse in={expanded}>
          <Box
            component="pre"
            sx={{ m: 0, mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'grey.100', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto', maxHeight: 500 }}
          >
            {content}
          </Box>
        </Collapse>
      ) : (
        <Box
          component="pre"
          sx={{ m: 0, mt: 0.5, p: 1, borderRadius: 1, bgcolor: 'grey.100', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {content}
        </Box>
      )}
    </Box>
  )
}

function formatToolInput(name: string, input: Record<string, unknown> | null): React.ReactNode {
  if (!input) return null

  switch (name) {
    case 'Read':
      return (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
          {String(input.file_path ?? '')}
          {input.offset ? ` (lines ${String(input.offset)}–${Number(input.offset) + Number(input.limit ?? 0)})` : ''}
        </Typography>
      )
    case 'Bash':
      return (
        <Box
          component="pre"
          sx={{ m: 0, p: 1, borderRadius: 1, bgcolor: 'grey.900', color: 'grey.100', fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto', maxHeight: 300 }}
        >
          {String(input.command ?? '')}
        </Box>
      )
    case 'Write':
      return (
        <Box>
          <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
            {String(input.file_path ?? '')}
          </Typography>
          {!!input.content && <CollapsibleCode content={String(input.content)} label="content" />}
        </Box>
      )
    case 'Edit':
      return (
        <Box>
          <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
            {String(input.file_path ?? '')}
          </Typography>
          {!!input.old_string && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="error.main">- old:</Typography>
              <Box component="pre" sx={{ m: 0, p: 0.5, bgcolor: 'error.50', borderRadius: 0.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {String(input.old_string)}
              </Box>
            </Box>
          )}
          {!!input.new_string && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="success.main">+ new:</Typography>
              <Box component="pre" sx={{ m: 0, p: 0.5, bgcolor: 'success.50', borderRadius: 0.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {String(input.new_string)}
              </Box>
            </Box>
          )}
        </Box>
      )
    case 'Grep':
      return (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
          /{String(input.pattern ?? '')}/ in {String(input.path ?? input.glob ?? '.')}
          {input.type ? ` (type: ${String(input.type)})` : ''}
        </Typography>
      )
    case 'Glob':
      return (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
          {String(input.pattern ?? '')}
          {input.path ? ` in ${String(input.path)}` : ''}
        </Typography>
      )
    case 'WebSearch':
      return <Typography variant="body2" fontStyle="italic">{String(input.query ?? '')}</Typography>
    case 'WebFetch':
      return <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{String(input.url ?? '')}</Typography>
    case 'Task':
      return (
        <Box>
          <Typography variant="body2" fontSize="0.85rem">{String(input.description ?? '')}</Typography>
          {!!input.prompt && <CollapsibleCode content={String(input.prompt)} label="prompt" />}
        </Box>
      )
    default:
      return (
        <Box
          component="pre"
          sx={{ m: 0, p: 1, borderRadius: 1, bgcolor: 'grey.100', fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto', maxHeight: 300 }}
        >
          {JSON.stringify(input, null, 2)}
        </Box>
      )
  }
}

function SystemEntry({ entry }: { entry: SystemEntryData }) {
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 1.5, backgroundColor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
      <Typography variant="caption" color="info.main" fontWeight={600}>
        SYSTEM {entry.subtype ? `/ ${entry.subtype.toUpperCase()}` : ''}
      </Typography>
      <Box sx={{ mt: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {entry.model && (
          <Box>
            <Typography variant="caption" color="text.secondary">Model</Typography>
            <Typography variant="body2">{entry.model}</Typography>
          </Box>
        )}
        {entry.cwd && (
          <Box>
            <Typography variant="caption" color="text.secondary">Working Dir</Typography>
            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{entry.cwd}</Typography>
          </Box>
        )}
        {entry.permission_mode && (
          <Box>
            <Typography variant="caption" color="text.secondary">Permissions</Typography>
            <Typography variant="body2">{entry.permission_mode}</Typography>
          </Box>
        )}
      </Box>
      {entry.tools && entry.tools.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Tools</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
            {entry.tools.map((tool) => (
              <Chip key={tool} label={tool} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
            ))}
          </Box>
        </Box>
      )}
      {entry.agents && entry.agents.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Agents</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
            {entry.agents.map((agent) => (
              <Chip key={agent} label={agent} size="small" variant="outlined" color="secondary" sx={{ height: 22, fontSize: '0.7rem' }} />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  )
}

function AssistantTextEntry({ entry }: { entry: AssistantTextEntryData }) {
  return (
    <Paper elevation={0} sx={{ p: 2, mb: 1.5, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>ASSISTANT</Typography>
        {entry.usage && (
          <Typography variant="caption" color="text.disabled">
            {formatTokens(entry.usage.input_tokens)} in / {formatTokens(entry.usage.output_tokens)} out
          </Typography>
        )}
      </Box>
      <Markdown variant="body2">{entry.text}</Markdown>
    </Paper>
  )
}

function ToolCallEntry({ entry }: { entry: ToolCallEntryData }) {
  const [resultExpanded, setResultExpanded] = useState(false)
  const resultLineCount = entry.result ? entry.result.split('\n').length : 0
  const isLongResult = resultLineCount > 10

  return (
    <Paper elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: entry.is_error ? 'error.light' : 'divider', overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Chip label={entry.name} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
        {entry.is_error && <Chip label="error" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />}
      </Box>
      {entry.input && (
        <Box sx={{ p: 1.5, borderBottom: entry.result ? '1px solid' : 'none', borderColor: 'divider' }}>
          {formatToolInput(entry.name, entry.input)}
        </Box>
      )}
      {entry.result && (
        <Box sx={{ p: 0 }}>
          <Box
            sx={{
              px: 1.5, py: 0.75, display: 'flex', alignItems: 'center',
              cursor: isLongResult ? 'pointer' : 'default',
              bgcolor: entry.is_error ? 'error.50' : 'transparent',
              '&:hover': isLongResult ? { bgcolor: 'action.hover' } : {},
            }}
            onClick={() => isLongResult && setResultExpanded(!resultExpanded)}
          >
            <Typography variant="caption" color="text.secondary">Result ({resultLineCount} lines)</Typography>
            {isLongResult && (
              <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                {resultExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            )}
          </Box>
          {isLongResult ? (
            <Collapse in={resultExpanded}>
              <Box component="pre" sx={{ m: 0, px: 1.5, pb: 1.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto', maxHeight: 600, color: entry.is_error ? 'error.main' : 'text.primary' }}>
                {entry.result}
              </Box>
            </Collapse>
          ) : (
            <Box component="pre" sx={{ m: 0, px: 1.5, pb: 1.5, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: entry.is_error ? 'error.main' : 'text.primary' }}>
              {entry.result}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  )
}

function UserTextEntry({ entry }: { entry: UserTextEntryData }) {
  const [expanded, setExpanded] = useState(false)
  const lineCount = entry.text.split('\n').length
  const isLong = lineCount > 20 || entry.text.length > 1000

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 1.5, ml: 4, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>USER</Typography>
        {isLong && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="caption" color="primary">{expanded ? 'Show less' : 'Show more'}</Typography>
            <IconButton size="small" sx={{ ml: 0.25, p: 0 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>
        )}
      </Box>
      {isLong && !expanded ? (
        <Box sx={{ maxHeight: 200, overflow: 'hidden', position: 'relative' }}>
          <Markdown variant="body2">{entry.text}</Markdown>
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
            background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))',
            pointerEvents: 'none',
          }} />
        </Box>
      ) : (
        <Markdown variant="body2">{entry.text}</Markdown>
      )}
    </Paper>
  )
}

function EntryContent({ entry }: { entry: LogEntry }) {
  switch (entry.type) {
    case 'system':
      return <SystemEntry entry={entry as SystemEntryData} />
    case 'assistant_text':
      return <AssistantTextEntry entry={entry as AssistantTextEntryData} />
    case 'tool_call':
      return <ToolCallEntry entry={entry as ToolCallEntryData} />
    case 'user_text':
      return <UserTextEntry entry={entry as UserTextEntryData} />
    default:
      return (
        <Paper elevation={0} sx={{ p: 1, mb: 1.5, border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.disabled">Unknown entry type: {entry.type}</Typography>
          {'raw' in entry && entry.raw && (
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              {entry.raw}
            </Typography>
          )}
        </Paper>
      )
  }
}

export function LogEntryComponent({ entry, highlighted }: { entry: LogEntry; highlighted?: boolean }) {
  const [jsonOpen, setJsonOpen] = useState(false)

  return (
    <Box
      id={`entry-${entry.index}`}
      sx={{
        position: 'relative',
        '&:hover .json-preview-btn': { opacity: 1 },
        ...(highlighted ? {
          '@keyframes highlightFade': {
            '0%': { backgroundColor: 'rgba(59, 130, 246, 0.15)' },
            '100%': { backgroundColor: 'transparent' },
          },
          animation: 'highlightFade 2s ease-out',
        } : {}),
      }}
    >
      <EntryContent entry={entry} />
      <Tooltip title="View raw JSON" placement="left">
        <IconButton
          className="json-preview-btn"
          size="small"
          onClick={() => setJsonOpen(true)}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            opacity: 0,
            transition: 'opacity 0.15s',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            width: 28,
            height: 28,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <DataObjectIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Dialog open={jsonOpen} onClose={() => setJsonOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Entry JSON — {entry.type}
          </Typography>
          <IconButton size="small" onClick={() => setJsonOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              fontSize: '0.8rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'auto',
              maxHeight: '70vh',
              bgcolor: 'grey.50',
            }}
          >
            {JSON.stringify(entry, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
